import { useRecoilState, useSetRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../../hooks";
import CustomIcon from "../CustomIcon/CustomIcon";
import TextInput from "../TextInput/TextInput";
import Button from "../Button/Button";
import { IPageDTOResponse } from "@/interfaces/IPageDTO";
import {
  GlobalSearchInfoAtom,
  GlobalSearchResultAtom,
  GlobalSearchTermAtom,
} from "@/atoms";

const GlobalSearchBar: React.FC = () => {
  const { i18n, t } = useTranslation();
  const lang = i18n.language;
  const [isMobile] = useIsMobile();
  const [searchTerm, setSearchTerm] = useRecoilState(GlobalSearchTermAtom);
  const setGlobalSearchResult = useSetRecoilState(GlobalSearchResultAtom);
  const setGlobalSearchInfo = useSetRecoilState(GlobalSearchInfoAtom);

  const fullText =
    lang === "it"
      ? "%27%20AND%20_fulltext+like+%27"
      : "%27%20AND%20_fulltext.en+like+%27";

  const handleSearch = () => {
    setGlobalSearchInfo((prev) => ({
      ...prev,
      callInProgress: true,
      searchStarted: true,
    }));

    fetch(
      `${process.env.NEXT_PUBLIC_BE_BASE_PATH}/rest/v1/SEA-LinMxp/contents/abstractPage?query=xtaxAirports.ti.label=%27${process.env.NEXT_PUBLIC_AIRPORT_REFERENCE}${fullText}${searchTerm}%27`
    )
      .then((res) => res.json())
      .then((data: IPageDTOResponse) => {
        setGlobalSearchInfo((prev) => ({
          ...prev,
          resultListInfo: data.resultInfo,
        }));
        setGlobalSearchResult(data.data.filter((d) => d.lblSeoTitle[lang]));
      })
      .finally(() => {
        setGlobalSearchInfo((prev) => ({ ...prev, callInProgress: false }));
      })
      .catch((error) => {
        setGlobalSearchInfo((prev) => ({
          ...prev,
          resultListInfo: null,
          callInProgress: false,
        }));
        console.error("[GlobalSearchBar] Error ->", error);
      });
  };

  const handleReset = () => {
    setSearchTerm("");
    setGlobalSearchInfo((prev) => ({
      ...prev,
      resultListInfo: null,
      searchStarted: false,
    }));
    setGlobalSearchResult([]);
  };

  return (
    <div className={`${isMobile ? "" : "gap-2"} flex`}>
      <div className="searchBar desk:h-fit bg-gray-200 rounded-xl rounded-r-none desk:rounded-r-xl w-[85%] desk:w-[80%]">
        <TextInput
          className="leading-7 text-gray-900"
          placeholder={t("globalSearch.search").toString()}
          onKeyDown={(e: KeyboardEvent) =>
            e.key.toLowerCase() === "enter" ? handleSearch() : null
          }
          onChange={(e: any) => {
            setSearchTerm(e.target?.value);
            if (e.target?.value === "") handleReset();
          }}
          generalBgColor="#E6E6E6"
          inputBgColor="#E6E6E6"
          postfix={
            searchTerm && searchTerm.length ? (
              <SearchBarChip text={t("globalSearch.delete")} onClick={handleReset} />
            ) : null
          }
          value={searchTerm}
          prefix={!isMobile ? <CustomIcon iconName="search" /> : null}
        />
      </div>

      <div className="desk:w-[20%] w-[15%]">
        {isMobile ? (
          <div
            className={`flex rounded-xl rounded-l-none h-full justify-center items-center ${
              !searchTerm
                ? "bg-gray-400 text-white cursor-default pointer-events-none"
                : "bg-gray-900 text-white cursor-pointer"
            }`}
            onClick={searchTerm ? handleSearch : () => {}}
          >
            <CustomIcon
              className="w-[20px]"
              iconName="search"
              color="white"
            />
          </div>
        ) : (
          <Button
            rounded="rounded-r-full desk:rounded-full desk:h-[3rem] items-center"
            className={`m-auto desk:px-8 ${
              !searchTerm
                ? "bg-gray-300 text-gray-800 cursor-default pointer-events-none"
                : "bg-black text-white hover:bg-lightblue"
            }`}
            onClick={searchTerm ? handleSearch : () => {}}
            text={t("globalSearch.searchBtn")}
          />
        )}
      </div>
    </div>
  );
};

interface ISearchBarChipProps {
  text: string;
  iconName?: string;
  onClick?: () => any;
}

const SearchBarChip: React.FC<ISearchBarChipProps> = ({
  text,
  iconName = "close",
  onClick = () => {},
}) => {
  return (
    <div
      onClick={onClick}
      className="flex text-gray-800 items-center cursor-pointer"
    >
      <div className="text-sm">{text}</div>
      <div className="h-[24px] w-[24px] mt-[2px]">
        <CustomIcon
          className="m-auto w-[24px] h-[24px] text-gray-700"
          iconName={iconName}
        />
      </div>
    </div>
  );
};

export default GlobalSearchBar;
